// Impact Analysis — public API (ARCHITECTURE.md §8).
//
// `analyzeImpact` is the entry point: given a DependencyGraph and a set of
// changed files, it walks the dependency edges (downstream by default),
// separates directly- from transitively-affected files, overlays contract
// information (artifacts, validations, agents, workflows), maps affected source
// files to their tests, and assembles an ImpactReport. Orchestration hints
// (§8.7) are produced by the §11 hints module; until then `hints` is empty.

import type { EdgeClassification } from "../types.js";
import type { DependencyGraph } from "../dependency/types.js";
import type { EvidenceType } from "../evidence/types.js";
import type {
  AffectedArtifact,
  AffectedEntry,
  ImpactDirection,
  ImpactReport,
  TestAssociation,
  TestAssociationMethod,
} from "./types.js";
import { traverseDownstream } from "./downstream.js";
import { traverseUpstream } from "./upstream.js";
import {
  resolveAffectedArtifacts,
  resolveAffectedWorkflows,
} from "./contract-overlay.js";
import { entryConfidence } from "./traverse.js";

export * from "./types.js";
export * from "./downstream.js";
export * from "./upstream.js";
export * from "./contract-overlay.js";
export * from "./callgraph.js";

export interface AnalyzeImpactOptions {
  graph: DependencyGraph;
  /** The changed files to analyze impact from. */
  changed: string[];
  /** Traversal direction. `downstream` answers "what breaks if this changes?". */
  direction: ImpactDirection;
  /** Maximum BFS depth to explore; default: unlimited. */
  maxDepth?: number;
  /**
   * Edge classifications to traverse; default: deterministic + candidate.
   * Restrict to `["deterministic"]` for a fast, candidate-free analysis.
   */
  edgeTypes?: EdgeClassification[];
  /** Evidence types to exclude when scoring entry relevance (Issue #21). */
  evidenceExclude?: EvidenceType[];
  /** Cap the number of affected entries, keeping the most relevant. */
  limit?: number;
  /** @deprecated Issue #21 — use `edgeTypes`. Honoured for backward compat. */
  minConfidence?: number;
  /** filePath → artifact IDs; defaults to `graph.artifactMapping`. */
  artifactMapping?: Map<string, string[]>;
  /** Optional agent-contracts Navigation Index for the contract overlay. */
  navigationIndex?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Test mapping (§8.5)
// ---------------------------------------------------------------------------

/** A file is a test if it carries a `.test`/`.spec` infix or lives under a
 * conventional test directory. Such files are not scanned for tests of their
 * own. */
function isTestFile(path: string): boolean {
  return (
    /\.(test|spec)\.[^/]+$/.test(path) ||
    path.startsWith("tests/") ||
    path.includes("/tests/") ||
    path.startsWith("__tests__/") ||
    path.includes("/__tests__/")
  );
}

interface TestCandidate {
  path: string;
  method: TestAssociationMethod;
}

/**
 * Candidate test paths for a source file, by naming/directory convention. The
 * candidates are validated against the actual file set by the caller.
 */
function testCandidates(sourcePath: string): TestCandidate[] {
  const slash = sourcePath.lastIndexOf("/");
  const dir = slash >= 0 ? sourcePath.slice(0, slash) : "";
  const file = slash >= 0 ? sourcePath.slice(slash + 1) : sourcePath;
  const dot = file.lastIndexOf(".");
  const base = dot >= 0 ? file.slice(0, dot) : file;
  const ext = dot >= 0 ? file.slice(dot) : "";
  const dirPrefix = dir ? `${dir}/` : "";

  const candidates: TestCandidate[] = [
    // Same directory, naming convention: src/foo.ts → src/foo.test.ts.
    { path: `${dirPrefix}${base}.test${ext}`, method: "naming_convention" },
    { path: `${dirPrefix}${base}.spec${ext}`, method: "naming_convention" },
    // Adjacent __tests__ directory.
    {
      path: `${dirPrefix}__tests__/${base}.test${ext}`,
      method: "directory_convention",
    },
    {
      path: `${dirPrefix}__tests__/${base}.spec${ext}`,
      method: "directory_convention",
    },
  ];

  // Mirrored test root: src/<rest>/foo.ts → tests|__tests__/<rest>/foo.test.ts.
  if (dir === "src" || dir.startsWith("src/")) {
    const rest = dir === "src" ? "" : dir.slice("src/".length);
    const restPrefix = rest ? `${rest}/` : "";
    for (const root of ["tests", "__tests__"]) {
      candidates.push(
        {
          path: `${root}/${restPrefix}${base}.test${ext}`,
          method: "directory_convention",
        },
        {
          path: `${root}/${restPrefix}${base}.spec${ext}`,
          method: "directory_convention",
        },
      );
    }
  }

  return candidates;
}

/**
 * Map affected source files to their test files, matching candidates against
 * the file paths present in the graph. Emits at most one TestAssociation per
 * (source, method) pair so the discovery method stays accurate.
 */
function buildTestMapping(
  sourcePaths: string[],
  fileSet: Set<string>,
): TestAssociation[] {
  const mapping: TestAssociation[] = [];

  for (const sourcePath of sourcePaths) {
    if (isTestFile(sourcePath)) continue;

    const byMethod = new Map<TestAssociationMethod, string[]>();
    for (const candidate of testCandidates(sourcePath)) {
      if (candidate.path === sourcePath) continue;
      if (!fileSet.has(candidate.path)) continue;
      const list = byMethod.get(candidate.method);
      if (list) {
        if (!list.includes(candidate.path)) list.push(candidate.path);
      } else {
        byMethod.set(candidate.method, [candidate.path]);
      }
    }

    for (const [method, testPaths] of byMethod) {
      mapping.push({ sourcePath, testPaths, method });
    }
  }

  return mapping;
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

/**
 * Analyze the impact of a set of changed files over a DependencyGraph
 * (ARCHITECTURE.md §8). Returns a fully-assembled ImpactReport.
 */
export async function analyzeImpact(
  options: AnalyzeImpactOptions,
): Promise<ImpactReport> {
  const {
    graph,
    changed,
    direction,
    maxDepth,
    edgeTypes,
    evidenceExclude,
    limit,
    minConfidence,
    artifactMapping = graph.artifactMapping,
    navigationIndex,
  } = options;

  // 1. Run the traversal in the requested direction. `limit` is applied below
  //    against the relevance-ranked set, not per-direction, so it is not
  //    forwarded here.
  const traversed: AffectedEntry[] =
    direction === "downstream"
      ? traverseDownstream({
          graph,
          changed,
          maxDepth,
          ...(edgeTypes ? { edgeTypes } : {}),
          ...(evidenceExclude ? { evidenceExclude } : {}),
          ...(minConfidence !== undefined ? { minConfidence } : {}),
        })
      : traverseUpstream({
          graph,
          files: changed,
          maxDepth,
          ...(edgeTypes ? { edgeTypes } : {}),
          ...(evidenceExclude ? { evidenceExclude } : {}),
          ...(minConfidence !== undefined ? { minConfidence } : {}),
        });

  // 2. Rank by evidence-derived relevance (Issue #21), then cap with `limit`.
  //    Ties fall back to the traversal's stable depth/path order so output is
  //    deterministic.
  const ranked = [...traversed].sort(
    (a, b) =>
      (b.relevanceScore ?? 0) - (a.relevanceScore ?? 0) ||
      a.depth - b.depth ||
      a.path.localeCompare(b.path) ||
      (a.symbolId ?? "").localeCompare(b.symbolId ?? ""),
  );
  const affected =
    limit !== undefined && limit >= 0 ? ranked.slice(0, limit) : ranked;

  // 3. Separate directly (depth 1) from transitively (depth > 1) affected.
  const directlyAffected = affected.filter((e) => e.depth === 1);
  const transitivelyAffected = affected.filter((e) => e.depth > 1);

  // 4. Resolve affected artifacts via the contract overlay. The changed files'
  //    own artifacts are affected too, so they are included. A changed seed may
  //    be a symbol id (filePath#name, §5.1); the file overlay and test mapping
  //    operate on its containing file.
  const changedFiles = changed.map((seed) => {
    const hash = seed.indexOf("#");
    return hash >= 0 ? seed.slice(0, hash) : seed;
  });
  const affectedFiles = [...changedFiles, ...affected.map((e) => e.path)];
  const affectedArtifacts: AffectedArtifact[] = resolveAffectedArtifacts({
    affectedFiles,
    artifactMapping,
    navigationIndex,
  });

  // 5. Resolve affected workflows.
  const affectedWorkflows = resolveAffectedWorkflows(
    affectedArtifacts,
    navigationIndex,
  );

  // 6. Build the test mapping over changed + affected source files.
  const fileSet = new Set<string>();
  for (const node of graph.nodes.values()) {
    if (node.kind === "file") fileSet.add(node.id);
  }
  const sourcePaths = [...new Set(affectedFiles)];
  const testMapping = buildTestMapping(sourcePaths, fileSet);

  // 7. Report-level confidence: the strongest affected path's chain confidence
  //    (each path's confidence is the floor of its evidence). Trivially 1.0
  //    when nothing is affected (no uncertain inference is involved).
  const maxConfidence =
    affected.length === 0
      ? 1
      : Math.max(...affected.map((e) => entryConfidence(e)));

  // Derive contract-level unions for the report header.
  const affectedValidations = [
    ...new Set(affectedArtifacts.flatMap((a) => a.requiredValidations)),
  ];
  const affectedAgents = [
    ...new Set(affectedArtifacts.flatMap((a) => a.ownerAgents)),
  ];

  // 8. Assemble the report. Orchestration hints are owned by §11; empty here.
  return {
    changed,
    direction,
    directlyAffected,
    transitivelyAffected,
    affectedArtifacts,
    affectedValidations,
    affectedAgents,
    affectedWorkflows,
    testMapping,
    hints: {},
    maxConfidence,
    generatedAt: new Date().toISOString(),
  };
}
